import { Injectable, EventEmitter } from '@angular/core';
import { MarkerInfo, MapMarker, UserMap } from '@lowcodeunit/lcu-map-common';
import { ThumbnailListModel } from '../models/thumbnail-list.model';
import { Subject } from 'rxjs';
import { Constants } from '../utils/constants/constants';
import { TempStorageService } from './temp.storage.service';
import { LocationLoader } from '../utils/location-loader';
import { UsersStateContext } from '../state/users/users-state-manager.context';
import { UsersState } from '../state/users/users-state.model';
import { Guid } from '@lcu/common';

@Injectable({
  providedIn: 'root'
})
export class MapService {
  // FIELDS
  /**
   * The primary map to be displayed - represents the current map being displayed
   */
  protected primaryMap: UserMap;

  /**
   * List of secondary maps available to choose as primary and also for use as layers in the primary map
   */
  protected mapList: Array<UserMap>;

  // PROPERTIES
  public State: UsersState;

  /**
   * Curated list of ambl_on location markers
   */
  public CuratedList: any;

/**
 * This value is changed when the user select something from the Atlas
 */
  public Layers: any;

  /**
   * Emits new primary map any time the maps (primary and secondary) are changed
   */
  public MapChanged: EventEmitter<UserMap>;

  /**
   * Subject that emits lat/lng for map panning
   */
  public LatLngEmitter: Subject<number[]> = new Subject<number[]>();

  public BasicInfoWindowEmitter: Subject<MapMarker> = new Subject<MapMarker>();

  /**
   * List of active secondary locations which are currently displayed on the current map
   */
  public VisibleLocations: Array<MapMarker> = [];

  public LegendLocations: Array<MapMarker> = [];

  public ThumbnailList: Array<ThumbnailListModel>;

  /**
   * The list of available markers for use in the map
   */
  public MarkerList: MarkerInfo[] = Constants.DEFAULT_MARKER_INFO;

/**
 * The hamburger menu subject to detect click event
 */
  public HamburgerMenuSubject = new Subject<boolean>();

  public ApiRoot: string;




  // CONSTRUCTORS

  constructor(protected tempStorage: TempStorageService, protected locLoader: LocationLoader, protected userState: UsersStateContext) {
    // this.primaryMap = this.State.UserLayers

    this.MapChanged = new EventEmitter<UserMap>();
    this.HamburgerMenuSubject = new Subject<boolean>();
    this.Layers = undefined;
    // temp: delete when state is faster:
    // console.log("constructor called");
  }

  // LIFE CYCLE
  ngOnInit(){
    this.mapList = this.State.UserMaps.filter(map => map.ID !== this.State.PrimaryMapID);
    // console.log("Map Service ",this.State.UserMaps);
  }

  // API METHODS

  public SetApiRoot(root: string):void{
    this.ApiRoot = root;
  }

  public GetApiRoot(): string{
    return this.ApiRoot;
  }
  // public SetState(state: UsersState){
  //   this.State = state;
  // }
  public UpdatePrimaryAndSecondaryMaps(primary: UserMap, secondary: Array<UserMap>){
    this.primaryMap = primary;
    this.mapList = secondary;
  }

  public GetHamburgerSubject(){
    return this.HamburgerMenuSubject;
  }

  public ToggleHamburgerMenu(){
    this.HamburgerMenuSubject.next(false);
  }

  public GetLegendLocations() {
    this.LegendLocations = this.tempStorage.GetLocationLegend();
    return this.LegendLocations;
  }

  /**
   * Returns this maplist list of thumbnails and their info
   */
  public GetThumbnailList() {
    return this.ThumbnailList;
  }

  /**
   * Returns the primary map to be displayed
   */
  public GetPrimaryMap(): UserMap {
    return this.primaryMap;
  }

  /**
   * Returns the list of secondary (non-primary) maps
   */
  public GetMapList(): Array<UserMap> {
    return this.mapList;
  }

  /**
   * Returns a list of all the maps in the db
   */
  // public GetAllMaps(): Array<UserMap> {
  //   return this.tempStorage.GetMapList();
  // }

  /**
   *
   * @param id The id of the map to retrieve
   *
   * Returns a specific map with a specific id
   */
  public GetMapById(id: string): UserMap {
    return this.mapList.find(map => map.ID === id);
  }

  /**
   * Returns the list of available markers for use in map
   *
   * This will call a back end service eventually and then format the results accordingly
   */
  public GetMarkerList(): MarkerInfo[] {
    return this.MarkerList;
  }

  /**
   *
   * @param map The map to add
   *
   * Adds the map to the map array
   */
  public AddMap(map: any): void {
    this.setMapsWithPrimaryId(map.id);
  }

  /**
   *
   * @param lat The latitude to pan to
   * @param lng The longitude to pan to
   *
   * Takes a lat/lng and emits event for map to listen to
   */
  public ChangeLatLng(lat: any, lng: any, zoom: number): void {
    this.LatLngEmitter.next([lat, lng, zoom]);
  }
  /**
   * @param marker
   *
   * Takes a marker from the legend and send to lcu-map
   */
  public DisplayBasicInfo(marker: MapMarker): void {
    this.BasicInfoWindowEmitter.next(marker);
  }

  /**
   *
   * @param list The list of visible locations
   *
   * When map updates the visible locations, this.VisibleLocations will be updated
   */
  public UpdateVisibleLocations(list: any): void {
    this.VisibleLocations = this.CheckForDuplicates(list);
    if (this.VisibleLocations.length > 0) {
      this.UpdateLegendLocations(this.VisibleLocations);
    }
  }

  /**
   * Updates the legend array
   * @param list
   */
  public UpdateLegendLocations(list: any): void {
    // console.log("Visible locations", this.VisibleLocations);
    this.LegendLocations = list;
  }

  CheckForDuplicates(list: Array<MapMarker>): Array<MapMarker> {
    // console.log("Checking for duplicates in: ", list);
    const temp: Array<MapMarker> = new Array<MapMarker>();

    const ids = list.map(function(item) {
      return item.ID;
    });
    const idsUnique = ids.filter(function(item, index) {
      return ids.indexOf(item) >= index;
    });
    // console.log("unique ids length = ", idsUnique.length);
    for (let i = 0; i < idsUnique.length; i++) {
      const tempMarker = this.tempStorage.GetLocationById(idsUnique[i]);
      // if its in the masterlist
      if (tempMarker) {
        temp.push(tempMarker);
      } else {
        temp.push(list.filter(item => item.ID === idsUnique[i])[0]);
      }
    }
    // console.log("Checked for duplicates", temp);
    return temp;
  }

  /**
   *
   * @param list saves the legend to local storage
   */
  // public SaveLegendLocations(list): void {
  //   this.tempStorage.SaveLocationLegend(list);
  // }

  /**
   *
   * @param map The map to replace the onld one with
   *
   * This replaces the map in the db with the map (based on id)
   */
  public ReplaceMap(map: UserMap): void {
    //this.tempStorage.ReplaceMap(map);
  }

  /**
   *
   * @param ListOfMaps The list of maps that you would like to get the thumbnails and other info for
   *
   * a thumbnail list contains the img url, the title of the map, and the lat and long of the orgin
   *
   */
  // public BuildThumbnailList(ListOfMaps: Array<UserMap>): Array<ThumbnailListModel> {
  //   const tempThumbnailList = new Array<ThumbnailListModel>();
  //   if(ListOfMaps){
  //   for (let i = 0; i < ListOfMaps.length; i++) {
  //     const temp = new ThumbnailListModel(
  //       ListOfMaps[i],
  //       this.getMapThumbnail(ListOfMaps[i], 200, 200),
  //       ListOfMaps[i].Title,
  //       +ListOfMaps[i].Latitude,
  //       +ListOfMaps[i].Longitude
  //     );
  //     tempThumbnailList.push(temp);
  //   }
  //   /**
  //    * This sorts based on the Title of the map so the thumbnail list is in alphabetical order
  //    */
  //   tempThumbnailList.sort((thumb1, thumb2) => {
  //     return thumb1.Title.toUpperCase() > thumb2.Title.toUpperCase() ? 1 : -1;
  //   });
  // }
  //   return tempThumbnailList;
  // }

  /**
   *
   * @param id The id of the new primary map
   *
   * Calls the protected method "setMapsWithPrimaryId"
   */
  public PrimaryMapChanged(map: any): void {
    //this.setMapsWithPrimaryId(id);
    let guid: Guid = map.ID;
    this.userState.SetSelectedMap(guid);
    this.primaryMap = map;
    //this.Layers = new Array<UserMap>(map);
  }

  /**
   * Deletes all the maps from the db (for testing purposes only)
   */
  public DeleteMaps(): void {
    this.tempStorage.DeleteMapList();
  }

  /**
   * Curated list of ambl_on location markers
   */
  // public GetIndividualMapsFromUserMaps(maps: any[]) {
  //   return maps.map(map => {
  //     return new UserMap({
  //       id: map.ID,
  //       title: map.Title,
  //       origin: { lat: map.Latitude, lng: map.Longitude },
  //       zoom: map.Zoom,
  //       locationList: []
  //     });
  //   });
  // }

  public GetMapMarkersFromLocations(locations: any[], mapId: string) {
    const mapMarkers = this.convertLocationDataToMapMarkers(locations);
    // console.log('locations after being converted...: ', mapMarkers)
    return this.formatLocationList(mapMarkers, mapId);
  }

   /**
   *
   * @param map The map configuration from which you want to create a thumbnail
   * @param width Optional width parameter (must also include heigth)
   * @param height Optional height parameter (must also include width)
   *
   * Takes a map configuration file and returns a google maps static map to be used as a thumbnail
   *
   * In order to specify height and width, you must specify BOTH, otherwise, the default is 300x300
   */
  public GetMapThumbnail(map: UserMap, width?: number, height?: number): string {
    return `https://maps.googleapis.com/maps/api/staticmap?center=${map.Latitude},${map.Longitude}
    &zoom=${map.Zoom - 2}
    &size=${height && width ? width : 300}x${height && width ? height : 300}
    &key=AIzaSyAsKh4_TXpYV57SBs7j3b6qFcJUG6fNHoU`;
  }

  // HELPERS

  protected formatLocationList(locations: Array<any>, mapId: string): Array<MapMarker> {
    locations.forEach(loc => {
      (loc.map_id = mapId),
      (loc.id = loc.ID);
      loc.iconName = loc.iconName.toLowerCase();
      loc.iconName = loc.iconName === 'hotel' ? 'lodging' : loc.iconName;
      loc.iconName = loc.iconName.toLowerCase() === 'unesco whs' ? 'UNESCO' : loc.iconName;
    });
    return locations;
  }



  /**
   *
   * @param id The id of the new primary map
   *
   * Sets the primary map to that with the passed id,
   *
   * Sets the secondary maps to a list of all the maps minus that with the passed id
   *
   * Emits the new primary map
   */
  protected setMapsWithPrimaryId(id: string): void {
    this.MapChanged.emit(this.primaryMap);
  }

  protected convertLocationDataToMapMarkers(locations: any[]) {
    // console.log('locations being passed into "convertLocation...:"', locations)
    return locations.map(location => {
      return {
        ID: location.ID,
        address: location.Address,
        iconName: location.Icon,
        title: location.Title,
        lat: location.Latitude,
        lng: location.Longitude,
        phoneNumber: location.Telephone,
        town: location.Town,
        country: location.Country,
        // iconUrl:null
      };
    });
  }
}
